import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import AddIcon from 'mdi-react/AddIcon';
import AsteriskIcon from 'mdi-react/AsteriskIcon';
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon';
import CommentAccountOutlineIcon from 'mdi-react/CommentAccountOutlineIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import EditIcon from 'mdi-react/EditIcon';
import MoreVertIcon from 'mdi-react/MoreVertIcon';
import { object } from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { I18n } from 'utils/i18n';

import ListItemButton from '@mui/material/ListItemButton';

const MENU_CLASS = 'chat-flow-node-menu';
const HEADER_CLASS = 'chat-flow-node-header';

const getStyles = theme => ({
  root: {
    textAlign: 'left',
    overflow: 'visible',
    minWidth: 200,
    [`&:hover .${HEADER_CLASS} .${MENU_CLASS}`]: {
      visibility: 'visible',
    },
  },
  route: {
    position: 'relative',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    boxShadow: `0 0 0 0.5px ${theme.palette.secondary.light}`,
    borderRadius: '2px',
    minHeight: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [`&:hover .${MENU_CLASS}`]: {
      visibility: 'visible',
    },
  },
  routeSelected: {
    backgroundColor: theme.palette.secondary.extraLight,
  },
  handle: {
    right: -19,
  },
  item: {
    minHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: 'none',
    marginTop: 0,
    marginBottom: 0,
    marginRight: theme.spacing(4),
  },
  header: {
    backgroundColor: theme.palette.secondary.ultraLight,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    textAlign: 'center',
  },
  subHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: 1,
    alignItems: 'center',
  },
  title: {
    fontWeight: 'bold',
  },
  iconWrapper: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: '-5px',
    marginBottom: '-7px',
  },
  iconFinish: {
    display: 'flex',
    color: theme.palette.ok,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: '-6px',
    marginBottom: '-6px',
  },
  menu: {
    visibility: 'hidden',
  },
  more: {
    marginTop: '-7px',
    marginBottom: '-5px',
  },
  asterisk: {
    marginLeft: theme.spacing(0.25),
    verticalAlign: 'super',
  },
});

const ChatFlowNode = ({ data: { page, startPage } }) => {
  const { setEdges } = useReactFlow();

  const isStart = pageId => startPage === pageId;
  const start = isStart(page._id);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMenuOpened, setMenuOpened] = useState(null);
  const { context, setToDelete } = useContext(ChatFlowContext);
  const onSelectRoute = route => () => {
    context.set({ type: 'route', page: page._id, route });
  };
  const onSelectEntryFulfillment = () => {
    context.set({ page: page._id, type: 'entry-fulfillment' });
  };
  const addRoute = () => {
    context.set({ type: 'route', page: page._id, route: 'new' });
  };
  const deleteElement = e => {
    e.stopPropagation();
    const element = { ...isMenuOpened };
    setMenuOpened(null);
    setToDelete(element);
  };
  const editElement = e => {
    e.stopPropagation();
    setMenuOpened(null);
    if (isMenuOpened.page) {
      context.set({
        type: 'route',
        route: isMenuOpened._id,
        page: isMenuOpened.page,
      });
    } else {
      context.set({
        type: 'page',
        page: page._id,
      });
    }
  };
  const handleHover = useCallback(
    id => () => {
      setEdges(eds =>
        eds.map(edge => ({
          ...edge,
          selected: Boolean(id && edge.id === id),
        })),
      );
    },
    [setEdges],
  );
  const fulfillmentSel = context.stack?.find(
    c => c.page === page._id && c.type === 'entry-fulfillment',
  );
  return (
    <Card sx={styles.root} variant="outlined">
      {!start && <Handle type="target" position={Position.Left} isConnectable={false} />}
      <CardHeader
        className={HEADER_CLASS}
        sx={styles.header}
        title={page.name}
        titleTypographyProps={{ variant: 'body1', sx: styles.title }}
        action={
          !start && (
            <IconButton
              size="small"
              aria-label="settings"
              onClick={e => {
                setAnchorEl(e.target);
                setMenuOpened(page);
              }}
              className={MENU_CLASS}
              sx={styles.menu}
            >
              <MoreVertIcon />
            </IconButton>
          )
        }
      />
      <CardContent>
        <List>
          <ListSubheader disableGutters sx={styles.subHeader}>
            <div>{I18n.t('ChatFlowSettings.Entry fulfillment')}</div>
          </ListSubheader>
          <ListItemButton
            onClick={onSelectEntryFulfillment}
            sx={[styles.route, fulfillmentSel && styles.routeSelected]}
          >
            <ListItemText sx={styles.item}>
              {I18n.t('ChatFlowSettings.Edit fulfillment')}
            </ListItemText>
          </ListItemButton>
          <ListSubheader disableGutters sx={styles.subHeader}>
            <div>{I18n.t('ChatFlowSettings.Routes')}</div>
            <div>
              <IconButton size="small" edge="end" aria-label="add" onClick={addRoute}>
                <AddIcon />
              </IconButton>
            </div>
          </ListSubheader>
          {page.routes.map(route => {
            const name = route.intent?.name || I18n.t('ChatFlowSettings.Default route');
            const hasCondition = !!route.condition;
            const selected = context.stack?.find(c => c.route === route._id);
            return (
              <ListItemButton
                key={route._id}
                onClick={onSelectRoute(route._id)}
                sx={[styles.route, selected && styles.routeSelected]}
                onFocus={handleHover(route._id)}
                onBlur={handleHover()}
                onMouseOver={handleHover(route._id)}
                onMouseLeave={handleHover()}
              >
                <ListItemText sx={styles.item}>
                  {name}
                  {hasCondition && (
                    <Tooltip title={route.condition}>
                      <Box component="span" sx={styles.asterisk}>
                        <AsteriskIcon size={8} />
                      </Box>
                    </Tooltip>
                  )}
                </ListItemText>
                <Box className={MENU_CLASS} sx={styles.menu}>
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAnchorEl(e.target);
                      setMenuOpened({ ...route, name, page: page._id });
                    }}
                    size="small"
                    sx={styles.more}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                {route.handoffTo && (
                  <Tooltip title={route.handoffTo.name}>
                    <Box sx={styles.iconWrapper}>
                      <CommentAccountOutlineIcon />
                    </Box>
                  </Tooltip>
                )}
                {isStart(route.transition) && (
                  <Box sx={styles.iconFinish}>
                    <CheckCircleOutlineIcon />
                  </Box>
                )}
                {route.transition && !isStart(route.transition) && (
                  <Handle
                    style={styles.handle}
                    type="source"
                    position={Position.Right}
                    id={route._id}
                    isConnectable={false}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </CardContent>
      <Menu anchorEl={anchorEl} open={!!isMenuOpened} onClose={() => setMenuOpened(null)}>
        <MenuItem onClick={editElement}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <Typography variant="inherit">{I18n.t('ChatFlowSettings.Edit')}</Typography>
        </MenuItem>
        <MenuItem onClick={deleteElement}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <Typography variant="inherit">{I18n.t('ChatFlowSettings.Delete')}</Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
};

ChatFlowNode.propTypes = {
  data: object.isRequired,
};

export default ChatFlowNode;
